import type { TapeMm } from "@/lib/artikelEtikett";
import type { RenderedArtikelEtikett } from "@/lib/artikelEtikettRender";
import { etikettFileName } from "@/lib/artikelEtikettRender";
import { isElectronWithPrintRaw, printRawOnWindows } from "@/lib/electron";
import { buildPtP910btJob } from "@/lib/ptP910bt";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Brother erwartet die Bandbreite als Papierbreite, sonst wird oft 36 mm angenommen. */
export function buildEtikettPrintDocument(
  labels: RenderedArtikelEtikett[],
  tapeMm: TapeMm,
): string {
  const first = labels[0];
  const lengthMm = first?.widthMm ?? 42;
  const tape = tapeMm.toFixed(2);
  const length = lengthMm.toFixed(2);
  const pages = labels
    .map((label) => {
      const w = label.widthMm.toFixed(2);
      return `<div class="page"><img alt="${escapeHtml(label.item.heroNr)}" src="${label.dataUrl}" style="width:${w}mm;height:${tape}mm" /></div>`;
    })
    .join("");
  return `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <title>Artikel-Etiketten</title>
  <style>
    @page { margin: 0; size: ${tape}mm ${length}mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    .page {
      position: relative;
      width: ${tape}mm;
      height: ${length}mm;
      page-break-after: always;
      overflow: hidden;
    }
    .page:last-child { page-break-after: auto; }
    img {
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      transform-origin: top left;
      transform: translateX(${tape}mm) rotate(90deg);
    }
  </style>
</head>
<body>${pages}</body>
</html>`;
}

export type SystemPrintMode = "raw" | "html";

export async function printEtikettenViaSystemDialog(
  labels: RenderedArtikelEtikett[],
  tapeMm: TapeMm,
): Promise<SystemPrintMode> {
  if (!labels.length) throw new Error("Keine Etiketten zum Drucken.");
  if (isElectronWithPrintRaw()) {
    const err = await printRawOnWindows(buildPtP910btJob(labels, tapeMm));
    if (!err) return "raw";
    throw new Error(err);
  }
  await printEtikettenViaHtmlDialog(labels, tapeMm);
  return "html";
}

function printEtikettenViaHtmlDialog(
  labels: RenderedArtikelEtikett[],
  tapeMm: TapeMm,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!win || !doc) {
      iframe.remove();
      reject(new Error("Druckvorschau konnte nicht geöffnet werden."));
      return;
    }
    doc.open();
    doc.write(buildEtikettPrintDocument(labels, tapeMm));
    doc.close();
    const cleanup = () => {
      iframe.remove();
      resolve();
    };
    win.addEventListener("afterprint", cleanup, { once: true });
    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch (err) {
        iframe.remove();
        reject(err instanceof Error ? err : new Error("Systemdruck fehlgeschlagen."));
      }
    }, 80);
    window.setTimeout(cleanup, 60_000);
  });
}

export function downloadEtiketten(labels: RenderedArtikelEtikett[]): void {
  labels.forEach((label, index) => {
    window.setTimeout(() => {
      const link = document.createElement("a");
      link.href = label.dataUrl;
      link.download = etikettFileName(label.item);
      link.click();
    }, index * 200);
  });
}
